import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    Flex,
    Tooltip,
    HStack,
    Button,
    Text,
    AlertTitle,
} from '@chakra-ui/react';
import useColumnState from '../../../hooks/useColumnState';
import ApprovalCodesCreate from './Create';
import ApprovalCodesEdit from './Edit';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { useQuery } from 'hooks/useCustomQuery';
import walkthroughIds from './walkthroughIds';
import { Alert, AlertIcon } from '@/components/v4';
import { MdEdit } from 'react-icons/md';
import StateToasts from './StateToasts';
import { getApprovalCodesQuery } from '@/lib/data-router/loaders/approvalCodeLoader';
import TimeCellRenderer from 'components/tables/AGGrid/CustomCells/TimeCellRenderer';
import ActionMenu from 'components/presentational/ActionMenu';

const { ufAddApprovalCodeBtn } = walkthroughIds;

function ApprovalCodesHeader({ setApprovalCodesAction }) {
    return (
        <div style={{ paddingLeft: '20px' }}>
            <HStack spacing={2}>
                <Tooltip
                    label="Approval Code"
                    placement="bottom"
                    hasArrow
                    portalProps={{ containerRef: undefined }}>
                    <div
                        style={{
                            position: 'relative',
                            display: 'inline-block',
                        }}>
                        {' '}
                        <Button
                            size="sm"
                            width="140px"
                            onClick={() => setApprovalCodesAction('Create')}
                            data-walkthroughid={ufAddApprovalCodeBtn}>
                            + Approval Code
                        </Button>
                    </div>
                </Tooltip>
            </HStack>
        </div>
    );
}

export default function ApprovalCodes() {
    const [selectedRowData, setSelectedRowData] = useState();
    const [approvalCodesAction, setApprovalCodesAction] = useState('Get');
    const [successAction, setSuccessAction] = useState(null);
    const tableRef = useRef(null);

    const currentPartner = useSelector(
        (state) => state.navigationLists?.currentPartner,
    );
    const { darkMode } = useSelector((state) => state.settings);

    // Table columns with edit button as last column
    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Name',
                field: 'name',
                sort: 'desc',
            },
            {
                headerName: 'Code',
                field: 'code',
            },
            {
                headerName: 'Auto Approve',
                field: 'autoApprove',
                cellRenderer: (rowData) => {
                    // 0: None, 1: Trial, 2: Live
                    if (rowData.value === 1) return 'Trial';
                    if (rowData.value === 2) return 'Live';
                    return 'None';
                },
            },
            {
                headerName: 'Plan Name',
                field: 'planName',
            },
            {
                headerName: 'Trial Days',
                field: 'trialDays',
            },
            {
                headerName: 'Selective Sync Policy',
                field: 'selectiveSyncPolicy',
                cellRenderer: (rowData) => {
                    // 0: Disabled, 1: Enabled, 2: Forced
                    if (rowData.selectiveSyncPolicy === 1) return 'Enabled';
                    if (rowData.selectiveSyncPolicy === 2) return 'Forced';
                    return 'Disabled';
                },
            },
            {
                headerName: 'Valid To',
                field: 'validTo',
                cellRenderer: TimeCellRenderer,
            },
            {
                headerName: 'Action',
                field: 'edit',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                lockPosition: 'right',
                cellRenderer: (params) => {
                    const actionItems = [
                        {
                            label: 'Edit',
                            onClick: () => editApprovalCode(params.data),
                            icon: MdEdit,
                        },
                    ];
                    return <ActionMenu actionItems={actionItems} />;
                },
            },
        ],
        [],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    // Fetch approval codes
    const {
        refetch,
        isRefetching,
        isFetching,
        status,
        data: approvalCodes = [],
    } = useQuery(getApprovalCodesQuery(currentPartner));

    function editApprovalCode(rowData) {
        setSelectedRowData(rowData);
        setApprovalCodesAction('Edit');
    }

    useEffect(() => {
        if (currentPartner) {
            refetch();
        }
    }, [approvalCodesAction, currentPartner, refetch]);

    const tableDataObj = useMemo(
        () => ({
            columns,
            data: approvalCodes,
        }),
        [columns, approvalCodes],
    );

    if (!currentPartner) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    return (
        <>
            {approvalCodesAction === 'Get' && (
                <>
                    <StateToasts type={successAction} />
                    <Text
                        sx={{
                            fontSize: '20',
                            fontWeight: '500',
                            marginBottom: '15px',
                        }}>
                        Approval Codes
                    </Text>
                    <AGGridMain
                        gridRef={tableRef}
                        tableData={tableDataObj}
                        masterDetail={false}
                        isLoading={status === 'loading'}
                        isFetching={isFetching}
                        isRefetching={isRefetching}
                        titleName="Approval Codes"
                        refetch={refetch}
                        onColumnVisible={onColumnVisible}
                        extraComponents={
                            <Flex gap="4">
                                <ApprovalCodesHeader
                                    setApprovalCodesAction={
                                        setApprovalCodesAction
                                    }
                                />
                            </Flex>
                        }
                    />
                </>
            )}
            {approvalCodesAction === 'Create' && (
                <ApprovalCodesCreate
                    setApprovalCodesAction={setApprovalCodesAction}
                    setSuccessAction={setSuccessAction}
                />
            )}
            {approvalCodesAction === 'Edit' && (
                <ApprovalCodesEdit
                    setSuccessAction={setSuccessAction}
                    setApprovalCodesAction={setApprovalCodesAction}
                    rowData={selectedRowData}
                    darkMode={darkMode}
                />
            )}
        </>
    );
}
